// THE LINE-ITEMS TABLE, EVERYWHERE ONE IS SHOWN (app.md §8): the Import
// Control editor, the approval tiers, Export Control, Manual Entry, the
// read-only modal steps and the expanded queue card. Every column ruling —
// which columns, in what order, under what header, what each icon does, the
// bulk row and the footer — lands here once (code review D-13,
// D-284). Until 2026-10-03 the editor
// drew a second copy of this table of its own.
//
// Two row sources, one row model (`TableLine`):
//
// - READ: the stored lines (`lineItems`). A cell is a picker where the caller
//   passes that half's write (`onAccountChange`, `onDepartmentChange`), a
//   category picker where it passes `onCategoryChange`, and a shaded locked box
//   or plain text otherwise (`LockedValue`).
// - EDIT: the Import Control editor's rows (`editor`), which the editor owns
//   and saves. Description, Price and Quantity are inputs, Account and
//   Department pickers, and each row adds Delete; an Add Line Item button sits
//   under the table, and the bulk row offers Combine (D-228 to D-230, D-242).
//
// The footer is a pass/fail "Reconciles" / "Doesn't reconcile" pill, then
// "Invoice Subtotal:" and the summed lines (owner, 2026-08-14). The check is
// the release gate's: lines + tax + shipping against the Total, within three
// cents (D-151). Its "View With Tax & Shipping Distributed" toggle is a view
// preference of its own (owner, 2026-08-14): on, two locked columns show each
// line's share of the two header charges — the Point Click Care CSV's own
// figure for that line, cent for cent (D-40, D-151) — and the line's Total
// carries both.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent as ReactFocusEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Body1,
  Button,
  Checkbox,
  Input,
  Spinner,
  Switch,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular } from '@fluentui/react-icons'
import type { AssociatedEntity, Flag, Invoice, InvoiceLineItem, LineItemTransportationDetail } from '../lib/types'
import {
  checkAmounts,
  detailCategoryLabel,
  filterMoneyInput,
  formatCurrency,
  formatPrice,
  groupedMoney,
  groupedPrice,
  lineItemCodingLabel,
  priceText,
  sumKnownLineTotals,
  toNumber,
  type LineItemDetailScreen,
} from '../lib/format'
import { additionalDetailSections, type SourceSystem } from '@ap/shared'
import {
  associatedEntitiesState,
  isBlank,
  marketingCategoryEditableAt,
  additionalDetailState,
  showRequiredBorder,
} from '../lib/required'
import { fieldBorder, flaggedTooltip, lineFieldFlags, type LineField } from '../lib/flagged'
import { TINT_PILLS, useMoneyInputStyles, useNumberInputStyles, useRequiredStyles, useSummaryLabelStyles } from '../lib/theme'
import { distributeCharges, distributedGrandTotal } from '../lib/taxDistribution'
import { DotPill } from './Badges'
import { CODING_PICKER_PLACEHOLDER, EntityPicker, type EntityOption } from './EntityPicker'
import { AssociatedEntitiesButton } from './AssociatedEntitiesButton'
import { FlagTooltip } from './FlagTooltip'
import { AdditionalDetailPopover } from './AdditionalDetailPopover'
import { isCapitalEquipmentOnlyAccount, loadAssociatedEntities, searchDepartments, searchAccounts } from '../lib/data'
import { useHalfLabels } from '../lib/hooks'
import { capitalEquipmentAllowed } from '../lib/constants'
import { bulkApplyScope, useLineSelection, type BulkChoice, type CombineInput } from '../lib/lineItemBulk'
import { rowKey, type LineItemState } from '../lib/intakeSaveBody'
import { codingFocusOnEnter, codingFocusOnLeave, codingFocusToRestore, refusalFor, type CodingFocus, type CodingRefusal } from '../lib/pickerCommit'
import type { LineDetailState } from '../lib/invoiceEditorState'
import { LineItemBulkBar, type BulkSelectedLine } from './LineItemBulkBar'
import {
  EDITOR_WIDTHS,
  READ_ONLY_WIDTHS,
  editorLineTableMinWidth,
  readOnlyDetailsWidth,
  readOnlyLineTableMinWidth,
} from '../lib/lineItemColumns'

const useStyles = makeStyles({
  panel: { padding: '2px 0' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // The table scrolls inside its own box rather than widening a narrow modal
  // (QA Q17).
  tableScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // Fixed layout: sized columns take their width and the text columns split
  // the rest; the inline `minWidth` (`lib/lineItemColumns.ts`) stops the text
  // columns being crushed (QA 2026-09-30, C1-01 and C2-02). Header labels clip
  // with an ellipsis rather than spilling into the next column.
  table: {
    tableLayout: 'fixed',
    width: '100%',
    fontVariantNumeric: 'tabular-nums',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // The read-only table's small uppercase headers.
  head: {
    '& .fui-TableHeaderCell__button': {
      fontSize: '10.5px',
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground3,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px', // matches SUMMARY_AMOUNT_PAD — see theme.ts's `amount`
    marginTop: '10px',
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  num: { textAlign: 'right' },
  // A box fills its cell and may shrink inside a fixed-layout table: Fluent's
  // `Input` ships `min-width: 160px`, which would set the numeric columns' floor.
  cellInput: { width: '100%', minWidth: 0 },
  // Right-aligned numeric headers: `textAlign` on the cell is inert, because
  // the label lives in an inner `.fui-TableHeaderCell__button` (app/README.md).
  numHeader: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  // The two revealed charge columns on a flat (unboxed) mount: dimmed, because
  // they are derived shares, not amounts anyone keyed.
  derivedNum: { textAlign: 'right', color: tokens.colorNeutralForeground3 },
  footerControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  // The icons at the end of a row: Additional Detail, Associated Entities,
  // and in the editor Delete (D-154).
  iconCell: { display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' },
  // No `display` on the cell itself: Fluent's TableCell sets `height: 34px`,
  // a floor on a table cell but enforced on a flex item, so a flex cell clipped
  // its picker. The flex row is the inner block.
  codingCell: { minWidth: 0 },
  codingCellInner: { display: 'flex', alignItems: 'flex-start', gap: '2px', minWidth: 0 },
  pickerStack: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0, flexGrow: 1 },
  selectCell: { width: `${READ_ONLY_WIDTHS.select}px` },
  addRow: { display: 'flex', paddingTop: '4px' },
})

/**
 * ONE LOOK FOR EVERY LOCKED FIELD IN THE INVOICE MODAL (owner item 2,
 * 2026-08-28): a locked value is the same shaded `readOnly` + `filled-darker`
 * box as the header panel's. Off where the table offers no control at all —
 * the expanded queue card, which is a gallery, not a form.
 */
function LockedValue({
  boxed,
  value,
  align,
  ariaLabel,
}: {
  boxed: boolean
  value: string
  align?: 'money' | 'number'
  ariaLabel?: string
}) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  const ns = useNumberInputStyles()
  if (!boxed) return <>{value}</>
  return (
    <Input
      className={s.cellInput}
      size="small"
      readOnly
      appearance="filled-darker"
      value={value}
      // The full text stays reachable on hover; a description often outruns its column.
      title={value}
      aria-label={ariaLabel}
      input={align === 'money' ? { className: ms.moneyText } : align === 'number' ? { className: ns.numText } : undefined}
    />
  )
}

/**
 * THE IMPORT CONTROL EDITOR'S ROWS AND WRITES — the table's editable row
 * model. `EditableInvoiceForm` owns the state and the saves; the table draws
 * the rows and calls back. Field keys are `description:<id>`, `price:<id>` and
 * `quantity:<id>`, the editor's focus and save-on-blur keys.
 */
export interface LineItemEditor {
  rows: readonly LineItemState[]
  details: Readonly<Record<string, LineDetailState>>
  /** The stored lines by id, for the Associated Entities pop-up. */
  storedLines: ReadonlyMap<string, InvoiceLineItem>
  /** Every control locked: an invoice at a status this editor does not cover. */
  locked: boolean
  /** The invoice came from another system (`isLockedSource`, D-380): each
      line's Account and Department stay open, and nothing else is offered —
      no typing in a line, no adding, removing or combining lines. */
  sourceLocked: boolean
  /** The flags behind the yellow borders (D-248). */
  flags: readonly Flag[]
  /** Where the row icons and the bulk row mount their pop-ups. */
  popupMountNode: HTMLElement | null
  setDescription: (id: string, value: string) => void
  setAmounts: (id: string, amounts: { price?: string; quantity?: string }) => void
  /** A field gained focus, with its value then. */
  focusField: (key: string, value: string) => void
  /** A field lost focus, with its value now: the editor saves if it changed. */
  blurField: (key: string, value: string) => void
  /** Enter commits a field (owner item 9, 2026-08-28). */
  onFieldKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void
  pickAccount: (id: string, option: EntityOption) => void
  /** Empties the line's Account, and its Department with it (D-295). */
  clearAccount: (id: string) => void
  pickDepartment: (id: string, option: EntityOption) => void
  pickCategory: (id: string, option: EntityOption) => void
  removeRow: (id: string) => void
  addRow: () => void
  applyToRows: (ids: ReadonlySet<string>, choice: BulkChoice) => void
  combine: (ids: ReadonlySet<string>, input: CombineInput) => void
  /** A tag was written in the Associated Entities pop-up. */
  tagsChanged: () => void
}

/** One row, from either source. */
interface TableLine {
  id: string
  /** The row's React key and focus key: a new line keeps it when the save
      gives it its stored id, so the row is not remounted under the caret. */
  key: string
  number: number | null
  description: string
  accountId: string | null
  /** A label picked this session; the stored ids are labelled from the masters. */
  accountLabel?: string
  departmentId: string | null
  departmentLabel?: string
  /** Read: the stored price. Edit: the text in the box. */
  price: string | number | null
  quantity: string | number | null
  total: string | number | null
  marketing: { categoryId: string | null; label: string }
  /** Fleet Maintenance's own detail, read-only (D-381); `null` on every other line. */
  transportation: LineItemTransportationDetail | null
  /** The line's source system — which decides whether it shows that detail. */
  source: SourceSystem | null
  /** The stored line; a stub carrying the id for a line added this session. */
  item: InvoiceLineItem
  /** Stored on the server: what a category write and a tag need. */
  saved: boolean
  isNew: boolean
}

/** The yellow-border tooltip, on the editor's controls only: a read table
    draws no flag tooltips, and gains no wrapper around its pickers. */
function EditorTooltip({ editing, text, children }: { editing: boolean; text: string | undefined; children: ReactElement }) {
  return editing ? <FlagTooltip text={text}>{children}</FlagTooltip> : children
}

function lineFromStored(c: InvoiceLineItem): TableLine {
  const md = c.marketing_detail
  return {
    id: c.id,
    key: c.id,
    number: c.line_number ?? null,
    description: c.description ?? '—',
    accountId: c.account_id ?? null,
    departmentId: c.department_id ?? null,
    price: c.unit_price ?? null,
    quantity: c.quantity ?? null,
    total: c.total ?? null,
    marketing: {
      categoryId: md?.category_id ?? null,
      label: detailCategoryLabel(md?.category_id ?? null, md?.category ?? null, md?.category_context ?? null),
    },
    transportation: c.transportation_detail ?? null,
    source: c.source_system ?? null,
    item: c,
    saved: true,
    isNew: false,
  }
}

function lineFromEditor(
  r: LineItemState,
  d: LineDetailState | undefined,
  stored: InvoiceLineItem | undefined,
): TableLine {
  return {
    id: r.lineItemId,
    key: rowKey(r),
    number: r.sequence ?? null,
    description: r.description,
    accountId: r.accountId ?? null,
    accountLabel: r.accountLabel,
    departmentId: r.departmentId ?? null,
    departmentLabel: r.departmentLabel,
    price: r.price,
    quantity: r.quantity,
    total: r.total,
    marketing: {
      categoryId: d?.marketingCategoryId ?? null,
      label: detailCategoryLabel(d?.marketingCategoryId ?? null, d?.marketingCategory ?? null, d?.marketingCategoryContext ?? null),
    },
    transportation: d?.transportation ?? null,
    source: stored?.source_system ?? null,
    item: stored ?? ({ id: r.lineItemId } as InvoiceLineItem),
    // A line the server knows has a counter (`rowVersion`); one added this
    // session has none until the save that creates it answers.
    saved: typeof r.rowVersion === 'number',
    isNew: !!r.isNew,
  }
}

interface Props {
  /** The stored lines (read mode). Ignored when `editor` is given. */
  lineItems?: InvoiceLineItem[] | 'loading'
  /** The Import Control editor's rows and writes (edit mode). */
  editor?: LineItemEditor
  /** Which screen mounted this table: which Additional Detail a line shows
      and which empty required fields are red here (`../lib/required.ts`). */
  screen: LineItemDetailScreen
  /** The invoice: what the Associated Entities pop-up reads its tags against,
      and, without `entitiesByLineItem`, what this table loads them for. */
  invoice?: Invoice
  /** The invoice amounts the footer reconciles against and distributes. */
  subtotal?: number | string | null
  tax?: number | string | null
  shipping?: number | string | null
  total?: number | string | null
  /** The tags behind each line's Associated Entities icon (D-154), by line id.
      Absent, this table loads them itself for `invoice`. */
  entitiesByLineItem?: Record<string, AssociatedEntity[]>
  /** Read mode: the Account column is a picker, the account half-write. */
  onAccountChange?: (lineItem: InvoiceLineItem, accountId: string) => void
  /** Read mode: the Department column is a picker, the department half-write.
      Its options derive from the line's own account. */
  onDepartmentChange?: (lineItem: InvoiceLineItem, departmentId: string) => void
  /** Read mode: THE LINE-ITEM BULK BAR (D-228) — a box per line, Select All
      in the header, and the bulk row above the table (D-242): an Account
      (required) and a Department (optional) applied to every checked line.
      Where `onDepartmentChange` is passed and `onAccountChange` is not, the
      Account is locked by the route (D-305) and the row applies the
      Department alone (`bulkApplyScope`). With neither, no boxes render. The
      caller writes each line and throws a message on a refusal. No Combine:
      only Import Control adds and removes lines. */
  onBulkApply?: (lineItems: InvoiceLineItem[], choice: BulkChoice) => Promise<void>
  /** Read mode: a line's Additional Detail pop-up offers the Marketing
      Category picker where it may be written here (`categoryPickableAt`, D-192, D-216). */
  onCategoryChange?: (lineItem: InvoiceLineItem, categoryId: string) => void
  /** Read mode: the line whose coding write is in flight — its pickers wait. */
  savingLineItemId?: string
  /** Read mode: bumped by the caller when a coding write was REFUSED, to
      remount the pickers so each re-seeds from the stored value. A refused
      pick leaves the value unchanged, which is the one case `EntityPicker`'s
      own resync cannot see (QA walk, 2026-08-30). */
  pickerResetNonce?: number
  /** Read mode: the caller's message for a refused coding write, by the line
      and the control it was refused on (`'Account'` or `'Department'`). That
      picker names the message as its description (`aria-describedby`), so a
      screen reader reads the reason with the field (QA 2026-10-04, C1-09). */
  refusal?: CodingRefusal
  /** Read mode, with no coding write: `'code'` shows the composed Code
      column; `'halves'` shows Account and Department read-only;
      `'account-only'` shows Account alone. */
  codingDisplay?: 'code' | 'halves' | 'account-only'
  /** Read mode: draw every locked value as a shaded box (`LockedValue`). By
      default boxed exactly when the table offers a coding control; the modal
      passes `true` so its fully read-only steps match the header panel. */
  boxedFields?: boolean
  /** Read mode: where the pickers mount their listboxes, for a caller that is
      not inside a Dialog (the expanded queue card, owner item 22). Unset in a
      dialog, so `EntityPicker` resolves its own host. */
  pickerMountNode?: HTMLElement | null
}

export function InvoiceLineItemsTable({
  lineItems,
  editor,
  screen,
  invoice,
  subtotal,
  tax,
  shipping,
  total,
  entitiesByLineItem: entitiesProp,
  onAccountChange,
  onDepartmentChange,
  onBulkApply,
  onCategoryChange,
  savingLineItemId,
  pickerResetNonce = 0,
  refusal,
  codingDisplay = 'code',
  boxedFields: boxedFieldsProp,
  pickerMountNode,
}: Props) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  const ns = useNumberInputStyles()
  const rs = useRequiredStyles()
  const labels = useHalfLabels()
  // A REFUSED SAVE KEEPS YOUR PLACE (QA 2026-10-04, review of C2-06). A refusal
  // remounts the row's pickers (`pickerResetNonce`) so each re-seeds from its
  // stored value, and a remounted box loses the focus the old one had: Tab from
  // an Account the server refused (Capital Equipment under the floor) left focus
  // on the page. The last focused coding picker is remembered by its line and
  // name, and focus goes back to its new copy when the remount dropped it. A
  // click on blank space forgets it, so a refusal that lands after the reviewer
  // left on purpose does not pull focus back (`lib/pickerCommit.ts`).
  const tableRef = useRef<HTMLTableElement>(null)
  const lastCodingFocus = useRef<CodingFocus | null>(null)
  function rememberCodingFocus(e: ReactFocusEvent<HTMLTableElement>) {
    lastCodingFocus.current = codingFocusOnEnter(
      e.target.getAttribute('aria-label'),
      e.target.closest('tr')?.getAttribute('data-line-key'),
    )
  }
  function forgetCodingFocusOnDeparture(e: ReactFocusEvent<HTMLTableElement>) {
    lastCodingFocus.current = codingFocusOnLeave(lastCodingFocus.current, {
      relatedTarget: e.relatedTarget,
      targetConnected: e.target.isConnected,
    })
  }
  useLayoutEffect(() => {
    const active = document.activeElement
    const last = codingFocusToRestore(lastCodingFocus.current, {
      pickerResetNonce,
      focusOnNothing: !active || active === document.body,
    })
    if (!last) return
    const rows = tableRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-line-key]') ?? []
    const row = [...rows].find((r) => r.getAttribute('data-line-key') === last.line)
    row?.querySelector<HTMLInputElement>(`input[aria-label="${last.name}"]`)?.focus()
  }, [pickerResetNonce])
  const editing = editor !== undefined
  const locked = editor?.locked ?? false
  // A LOCKED SOURCE (D-380) freezes every line value but the two coding halves:
  // Description, Price and Quantity are read-only, and no line is added,
  // removed or combined. The server refuses the rest; this only stops offering it.
  const sourceLocked = editor?.sourceLocked ?? false
  const lineValuesLocked = locked || sourceLocked

  // EVERY HOOK RUNS BEFORE THE EARLY RETURNS below.
  const lines = useMemo<TableLine[]>(() => {
    if (editor) return editor.rows.map((r) => lineFromEditor(r, editor.details[r.lineItemId], editor.storedLines.get(r.lineItemId)))
    return Array.isArray(lineItems) ? lineItems.map(lineFromStored) : []
  }, [editor, lineItems])

  // THE TAGS BEHIND EACH LINE'S ASSOCIATED ENTITIES ICON (D-154). The modal and
  // the editor pass them; the queue card does not, so the table reads them
  // itself once its lines have loaded — a collapsed card mounts this table with
  // no lines, and a queue of them must not fire one read each.
  const [loadedEntities, setLoadedEntities] = useState<Record<string, AssociatedEntity[]>>({})
  const invoiceId = invoice?.id
  const linesLoaded = lines.length > 0
  useEffect(() => {
    if (entitiesProp !== undefined || !invoiceId || !linesLoaded) return
    let live = true
    loadAssociatedEntities(invoiceId)
      .then((rows) => {
        if (!live) return
        const grouped: Record<string, AssociatedEntity[]> = {}
        for (const e of rows) if (e.child_id) (grouped[e.child_id] ??= []).push(e)
        setLoadedEntities(grouped)
      })
      .catch(() => { if (live) setLoadedEntities({}) })
    return () => { live = false }
  }, [entitiesProp, invoiceId, linesLoaded])
  const entitiesByLineItem = entitiesProp ?? loadedEntities

  // The bulk bar's selection (D-228). It resets when another invoice opens and
  // drops a line the rows no longer hold.
  const selection = useLineSelection(useMemo(() => lines.map((l) => l.id), [lines]), invoiceId)
  const selected = useMemo(() => lines.filter((l) => selection.selectedIds.has(l.id)), [lines, selection.selectedIds])
  const [applying, setApplying] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  useEffect(() => setBulkError(null), [invoiceId])

  // ITEM 14(b) — the accounts on this table, under the $5,000 floor, whose only
  // department is Capital Equipment (`190110`): their Department picker has
  // nothing to offer. Looked up once per distinct account, at the top level,
  // because the lookup is async and a row cannot hold a hook.
  const capitalCandidateAccounts = useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .filter((l) => !!l.accountId && !capitalEquipmentAllowed(toNumber(l.total)))
            .map((l) => l.accountId as string),
        ),
      ),
    [lines],
  )
  const capitalCandidateKey = capitalCandidateAccounts.join('|')
  const [capitalOnlyAccounts, setCapitalOnlyAccounts] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    let cancelled = false
    Promise.all(
      capitalCandidateAccounts.map((accountId) =>
        isCapitalEquipmentOnlyAccount(accountId).then((isCapitalOnly) => [accountId, isCapitalOnly] as const),
      ),
    ).then((results) => {
      if (cancelled) return
      setCapitalOnlyAccounts(new Set(results.filter(([, only]) => only).map(([accountId]) => accountId)))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capitalCandidateAccounts is recomputed from capitalCandidateKey
  }, [capitalCandidateKey])

  // The footer toggle. A view preference: never sent, OFF on every mount, and
  // independent of the GL Summary's own (owner, 2026-08-14).
  const [distribute, setDistribute] = useState(false)
  // Which price boxes have focus: a focused box shows the raw text being typed,
  // a blurred one the comma-grouped price (owner item 12, 2026-08-18). State
  // never holds a comma.
  const [editingPrice, setEditingPrice] = useState<ReadonlySet<string>>(() => new Set())

  const applyBulk = useCallback(
    async (choice: BulkChoice) => {
      if (selected.length === 0) return
      setBulkError(null)
      setApplying(true)
      try {
        if (editor) editor.applyToRows(new Set(selected.map((l) => l.id)), choice)
        else if (onBulkApply) await onBulkApply(selected.map((l) => l.item), choice)
        // The boxes clear once the choice has landed, so a second Apply is a
        // new selection, not a repeat.
        selection.clear()
      } catch (e) {
        setBulkError(e instanceof Error ? e.message : String(e))
      } finally {
        setApplying(false)
      }
    },
    [editor, onBulkApply, selected, selection],
  )
  const combineSelected = useCallback(
    (input: CombineInput) => {
      if (!editor) return
      setBulkError(null)
      editor.combine(new Set(selected.map((l) => l.id)), input)
      selection.clear()
    },
    [editor, selected, selection],
  )

  if (!editing && lineItems === undefined) {
    return <div className={s.panel}><Spinner size="tiny" label="Loading line items…" /></div>
  }
  if (!editing && lineItems === 'loading') {
    return <div className={s.panel}><Spinner size="tiny" label="Loading line items…" /></div>
  }
  if (!editing && lines.length === 0) {
    return <div className={s.panel}><Body1 className={s.none}>No line items on this invoice.</Body1></div>
  }

  // WHICH COLUMNS. The editor shows Account and Department as pickers. A read
  // table shows each half as a picker where its write is passed, and otherwise
  // as `codingDisplay` asks — the composed Code, both halves, or Account alone.
  const editsAccount = editing || onAccountChange !== undefined
  const editsDepartment = editing || onDepartmentChange !== undefined
  const readOnlyCoding = !editsAccount && !editsDepartment
  const boxedFields = editing || (boxedFieldsProp ?? !readOnlyCoding)
  const showCode = readOnlyCoding && codingDisplay === 'code'
  const showAccount = !readOnlyCoding || codingDisplay !== 'code'
  const showDepartment = editsDepartment || (readOnlyCoding && codingDisplay === 'halves')
  // THE SELECT COLUMN renders only where the bar can act: the editor's rows
  // unless frozen, and a read table's where a coding half is open — the
  // Account (with the Department), or the Department alone (D-305). A hold
  // changes nothing here (D-282).
  const bulkScope = bulkApplyScope({ account: editsAccount, department: editsDepartment })
  const bulk = editing ? !locked : onBulkApply !== undefined && bulkScope !== null
  const offerCombine = editing && !sourceLocked
  const canPickCategories = editing || onCategoryChange !== undefined

  // The editor's columns are wider (inputs, three icons); the read table's
  // headers are small capitals. Every other ruling is shared.
  const widths = editing
    ? { select: EDITOR_WIDTHS.select, number: EDITOR_WIDTHS.number, account: EDITOR_WIDTHS.account, department: EDITOR_WIDTHS.department, price: EDITOR_WIDTHS.price, quantity: EDITOR_WIDTHS.quantity, tax: EDITOR_WIDTHS.tax, shipping: EDITOR_WIDTHS.shipping, total: EDITOR_WIDTHS.total, icons: EDITOR_WIDTHS.actions }
    : { select: READ_ONLY_WIDTHS.select, number: READ_ONLY_WIDTHS.number, account: undefined, department: undefined, price: READ_ONLY_WIDTHS.price, quantity: READ_ONLY_WIDTHS.quantity, tax: READ_ONLY_WIDTHS.tax, shipping: READ_ONLY_WIDTHS.shipping, total: READ_ONLY_WIDTHS.total, icons: readOnlyDetailsWidth(bulk) }
  const head = editing ? undefined : s.head
  const numHead = mergeClasses(head, s.numHeader)

  // THE TOGGLE, gated to known amounts: with none there is nothing to spread.
  // The editor's boxes are text, blank when cleared, and its Subtotal is always
  // derived, so there the three charges decide; a read table has the stored
  // values, null when unknown.
  const filled = (v: number | string | null | undefined) => v != null && String(v).trim() !== ''
  const amountsKnown = editing
    ? filled(tax) || filled(shipping) || filled(total)
    : subtotal != null || tax != null || total != null || shipping != null
  const taxDollars = toNumber(tax)
  const shippingDollars = toNumber(shipping)
  const printedTotal = total === undefined || total === null || String(total).trim() === '' ? null : toNumber(total)
  const distributing = amountsKnown && distribute
  // One allocation per charge across the LINES — the CSV's own grain (D-40) —
  // with the vendor's variance of up to three cents on the largest (D-151).
  const shares = new Map<string, { tax: number; shipping: number; total: number }>()
  if (distributing) {
    for (const d of distributeCharges(
      lines.map((l) => ({ id: l.id, total: toNumber(l.total), sequence: l.number ?? 0 })),
      taxDollars,
      shippingDollars,
      printedTotal,
    )) {
      shares.set(d.id, { tax: d.tax, shipping: d.shipping, total: d.total })
    }
  }

  // THE HEADER ROW, once, so the bulk box above the table shows the very same
  // headers and the two cannot drift (D-242). The box's copy has no select-all
  // box and no #: one header spans both, "Action", over its picker of Update
  // or Combine, which shows on every screen (owner, 2026-10-14).
  const lineHeader = (selectAll: boolean) => (
    <TableHeader>
      <TableRow>
        {bulk && !selectAll && (
          <TableHeaderCell colSpan={2} className={head} style={{ width: `calc(${widths.select + widths.number}px + 2 * ${tokens.spacingHorizontalS})` }}>
            Action
          </TableHeaderCell>
        )}
        {bulk && selectAll && (
          <TableHeaderCell className={mergeClasses(head, s.selectCell)} style={{ width: widths.select }}>
            <Checkbox
              aria-label="Select every line item"
              checked={selection.headerState}
              disabled={applying}
              onChange={(_e, d) => selection.setAll(d.checked === true)}
            />
          </TableHeaderCell>
        )}
        {(selectAll || !bulk) && <TableHeaderCell style={{ width: widths.number }} className={head}>#</TableHeaderCell>}
        <TableHeaderCell className={head}>Description</TableHeaderCell>
        {/* "Code" NAMES A WHOLE CODE, read-only, where a surface asks for it;
            every coding surface edits the two halves as their own columns, and
            "Code" never collides with the "Account" half (D-35, D-43, D-139). */}
        {showCode && <TableHeaderCell className={head}>Code</TableHeaderCell>}
        {showAccount && <TableHeaderCell className={head} style={{ width: widths.account }}>Account</TableHeaderCell>}
        {showDepartment && <TableHeaderCell className={head} style={{ width: widths.department }}>Department</TableHeaderCell>}
        <TableHeaderCell style={{ width: widths.price }} className={numHead}>Price</TableHeaderCell>
        <TableHeaderCell style={{ width: widths.quantity }} className={numHead}>Quantity</TableHeaderCell>
        {/* The two DERIVED charge columns, between Quantity and Total, the
            order the arithmetic reads. */}
        {distributing && <TableHeaderCell style={{ width: widths.tax }} className={numHead}>Tax</TableHeaderCell>}
        {distributing && <TableHeaderCell style={{ width: widths.shipping }} className={numHead}>Shipping</TableHeaderCell>}
        <TableHeaderCell style={{ width: widths.total }} className={numHead}>Total</TableHeaderCell>
        <TableHeaderCell style={{ width: widths.icons }} className={head} aria-label={editing ? 'Line actions' : 'Line details'} />
      </TableRow>
    </TableHeader>
  )
  const lineTableMinWidth = editing
    ? editorLineTableMinWidth({ select: bulk, distributing })
    : readOnlyLineTableMinWidth({ select: bulk, code: showCode, account: showAccount, department: showDepartment, distributing })
  const popupMountNode = editor ? editor.popupMountNode : pickerMountNode

  /** A line control's border: red when required and empty, else yellow when
      an unresolved flag is about it (D-248). The editor's alone: the flags are
      resolved by the time an invoice reaches any other step. */
  const lineBorder = (red: boolean, l: TableLine, field: LineField) => {
    if (!editor) return undefined
    const border = fieldBorder(red, lineFieldFlags(editor.flags, l.number, field))
    return border === 'required' ? rs.requiredEmpty : border === 'flagged' ? rs.flagged : undefined
  }
  const lineTip = (red: boolean, l: TableLine, field: LineField) =>
    editor && !red ? flaggedTooltip(lineFieldFlags(editor.flags, l.number, field)) : undefined

  const bulkSelected: BulkSelectedLine[] = selected.map((l) => ({
    id: l.id,
    total: l.total,
    accountId: l.accountId,
    marketingCategoryId: l.marketing.categoryId,
    marketingLabel: l.marketing.label,
  }))

  return (
    <div className={editing ? undefined : s.panel}>
      <div className={s.tableScroll}>
        {/* THE BULK BOX (D-242): above the table once a line is checked — its
            own copy of the headers over the proposed row, in the table's
            scroller, so the two scroll together. */}
        {bulk && selection.count > 0 && (
          <LineItemBulkBar
            selected={bulkSelected}
            offerCombine={offerCombine}
            scope={bulkScope ?? 'account'}
            screen={screen}
            columns={{ code: showCode, department: showDepartment, distributing }}
            header={lineHeader(false)}
            tableClassName={s.table}
            tableMinWidth={lineTableMinWidth}
            invoice={invoice}
            busy={applying}
            error={bulkError}
            mountNode={popupMountNode}
            onApply={applyBulk}
            onCombine={editing ? combineSelected : undefined}
            onClear={selection.clear}
          />
        )}
        <Table
          ref={tableRef}
          onFocusCapture={rememberCodingFocus}
          onBlurCapture={forgetCodingFocusOnDeparture}
          size="small"
          aria-label={editing ? 'Editable line items' : 'Line items'}
          className={s.table}
          style={{ minWidth: lineTableMinWidth }}
        >
          {lineHeader(true)}
          <TableBody>
            {lines.map((l) => {
              // ADDITIONAL DETAIL (D-154): what this line shows — the Marketing
              // Category its account's kind owes, and Fleet Maintenance's own
              // detail on a line copied from it (D-381, read-only) — who may
              // write the category here, and what is red. A line added this
              // session has nothing stored to write against, so its pop-up
              // says to save first.
              const sections = additionalDetailSections(labels.detailKind(l.accountId), l.source)
              const pickable = canPickCategories && !locked && l.saved
              const editable = { marketing: pickable && marketingCategoryEditableAt(screen) }
              const transportationDetail = sections.transportation ? l.transportation : null
              const other = additionalDetailState({
                sections,
                screen,
                editable,
                marketingCategoryId: l.marketing.categoryId,
                hasTransportationDetail: transportationDetail !== null,
              })
              // Associated Entities: written here only in the editor (D-216).
              // Red on an unmatched tag, whose flag holds the release.
              const tagsEditable = editing && !locked
              const entityState = associatedEntitiesState(entitiesByLineItem[l.id] ?? [], screen, tagsEditable)
              const live = !locked
              // Description, Price and Quantity: open in the editor unless the
              // status or a locked source closes them.
              const valuesLive = !lineValuesLocked
              const red = {
                description: showRequiredBorder('description', screen, isBlank(l.description), editing && valuesLive),
                // AN INACTIVE ACCOUNT COUNTS AS EMPTY wherever the Account can
                // still change (D-308): it must be changed before Release or
                // Approve. Where it is locked it is not red, and the invoice
                // proceeds as it is.
                account: showRequiredBorder(
                  'account',
                  screen,
                  !l.accountId || labels.accountInactive(l.accountId),
                  // Never red where it is locked (D-156) — past Import Control
                  // and Controller Approval, the Account is (D-305).
                  live && editsAccount,
                ),
                department: showRequiredBorder('department', screen, !l.departmentId, live && editsDepartment && !!l.accountId),
                price: showRequiredBorder('price', screen, isBlank(String(l.price ?? '')), editing && valuesLive),
                quantity: showRequiredBorder('quantity', screen, isBlank(String(l.quantity ?? '')), editing && valuesLive),
              }
              const saving = savingLineItemId === l.id
              const departmentBlocked =
                !l.accountId || (capitalOnlyAccounts.has(l.accountId) && !capitalEquipmentAllowed(toNumber(l.total)))
              const priceKey = `price:${l.key}`
              return (
                <TableRow key={l.key} data-line-key={l.key}>
                  {bulk && (
                    <TableCell className={s.selectCell}>
                      <Checkbox
                        aria-label={`Select line ${l.number ?? ''}`}
                        checked={selection.selectedIds.has(l.id)}
                        disabled={applying}
                        onChange={(_e, d) => selection.toggle(l.id, d.checked === true)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{l.number ?? '—'}</TableCell>
                  <TableCell>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.description, l, 'description')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.description, l, 'description'))}
                          size="small"
                          aria-label="Description"
                          value={l.description}
                          onChange={(_, d) => editor.setDescription(l.id, d.value)}
                          onFocus={() => { if (valuesLive) editor.focusField(`description:${l.key}`, l.description) }}
                          onBlur={() => { if (valuesLive) editor.blurField(`description:${l.key}`, l.description) }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={l.description} ariaLabel="Description" />
                    )}
                  </TableCell>
                  {showCode && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>{lineItemCodingLabel(l.item, labels.account(l.accountId))}</div>
                    </TableCell>
                  )}
                  {showAccount && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>
                        <EditorTooltip editing={editing} text={lineTip(red.account, l, 'account')}>
                          <div className={s.pickerStack}>
                            {editsAccount ? (
                              <EntityPicker
                                key={`account:${pickerResetNonce}`}
                                size="small"
                                ariaLabel="Account"
                                describedBy={refusalFor(refusal, l.id, 'Account')}
                                value={{ id: l.accountId ?? undefined, name: l.accountLabel ?? labels.account(l.accountId) }}
                                mountNode={editing ? undefined : pickerMountNode}
                                // The column header asks the question; the
                                // placeholder says what to do (D-242).
                                placeholder={CODING_PICKER_PLACEHOLDER}
                                locked={locked}
                                // Busy, not disabled, while the line saves: a
                                // disabled box throws focus to the page (C2-06).
                                busy={saving}
                                // RED wherever a required value is missing
                                // (owner, 2026-08-18).
                                invalid={red.account}
                                className={lineBorder(red.account, l, 'account')}
                                search={(t) =>
                                  searchAccounts(t).then<EntityOption[]>((es) =>
                                    es.map((e) => ({ id: e.account_id, primary: e.account_name, secondary: e.account_id })),
                                  )
                                }
                                onSelect={(o) => (editor ? editor.pickAccount(l.id, o) : onAccountChange?.(l.item, o.id))}
                                // Emptied in the editor, the Account clears (D-295);
                                // the read-mode pickers put the stored one back.
                                onClear={editor ? () => editor.clearAccount(l.id) : undefined}
                              />
                            ) : (
                              <LockedValue boxed={boxedFields} value={labels.account(l.accountId) ?? '—'} ariaLabel="Account" />
                            )}
                          </div>
                        </EditorTooltip>
                      </div>
                    </TableCell>
                  )}
                  {showDepartment && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>
                        <EditorTooltip editing={editing} text={lineTip(red.department, l, 'department')}>
                          <div className={s.pickerStack}>
                            {editsDepartment ? (
                              <EntityPicker
                                key={`department:${pickerResetNonce}`}
                                size="small"
                                ariaLabel="Department"
                                describedBy={refusalFor(refusal, l.id, 'Department')}
                                value={{ id: l.departmentId ?? undefined, name: l.departmentLabel ?? labels.department(l.departmentId) }}
                                mountNode={editing ? undefined : pickerMountNode}
                                // ALWAYS "Search" (owner, 2026-09-30 — D-242). The
                                // box is blocked until the line has an account,
                                // and under the Capital Equipment floor where
                                // `190110` is its account's only department.
                                // Blocked, not disabled: the block can arrive
                                // after Tab from a saved Account has put focus
                                // here, and a disabled box drops it (review of
                                // C2-06).
                                placeholder={CODING_PICKER_PLACEHOLDER}
                                locked={locked}
                                blocked={!locked && departmentBlocked}
                                // Busy, not disabled, during the line's save, so
                                // Tab from a saved Account lands here (C2-06).
                                busy={saving}
                                // RED when required here and empty (D-156) —
                                // at Import Control too since D-309 — and only
                                // once the account is set.
                                invalid={red.department}
                                className={lineBorder(red.department, l, 'department')}
                                // The row's key is its id, so a new account
                                // reuses this picker; naming the dependency is
                                // what makes it re-query.
                                optionsKey={l.accountId ?? undefined}
                                search={(t) =>
                                  // This line's own total, so Capital Equipment
                                  // is not offered under the floor (item 8).
                                  searchDepartments(t, l.accountId ?? undefined, toNumber(l.total)).then<EntityOption[]>((ds) =>
                                    ds.map((d) => ({ id: d.department_id, primary: d.department_name, secondary: d.department_id })),
                                  )
                                }
                                onSelect={(o) => (editor ? editor.pickDepartment(l.id, o) : onDepartmentChange?.(l.item, o.id))}
                              />
                            ) : (
                              <LockedValue boxed={boxedFields} value={labels.department(l.departmentId) ?? '—'} ariaLabel="Department" />
                            )}
                          </div>
                        </EditorTooltip>
                      </div>
                    </TableCell>
                  )}
                  {/* PRICE and QUANTITY are the inputs; TOTAL is derived from
                      them (D-4). `type="text"` for Price: a blurred box shows
                      "1,234.56", which `type="number"` would blank. */}
                  <TableCell className={s.num}>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.price, l, 'price')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.price, l, 'price'))}
                          size="small"
                          aria-label="Price"
                          type="text"
                          inputMode={lineValuesLocked ? undefined : 'decimal'}
                          contentBefore="$"
                          input={{ className: ms.moneyText }}
                          value={valuesLive && editingPrice.has(priceKey) ? String(l.price) : groupedPrice(String(l.price))}
                          onChange={(_, d) => editor.setAmounts(l.id, { price: filterMoneyInput(d.value) })}
                          onFocus={() => {
                            setEditingPrice((prev) => new Set(prev).add(priceKey))
                            if (valuesLive) editor.focusField(priceKey, String(l.price))
                          }}
                          onBlur={() => {
                            setEditingPrice((prev) => {
                              const next = new Set(prev)
                              next.delete(priceKey)
                              return next
                            })
                            if (!valuesLive) return
                            // Every decimal kept (D-249): 0.0049 stays 0.0049.
                            const f = priceText(String(l.price))
                            if (f !== l.price) editor.setAmounts(l.id, { price: f })
                            editor.blurField(priceKey, f)
                          }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={formatPrice(l.price)} align="money" ariaLabel="Price" />
                    )}
                  </TableCell>
                  {/* Quantity is not money — no `$`, no grouping — but it
                      right-aligns beside the amounts (owner item 1, 2026-08-28). */}
                  <TableCell className={s.num}>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.quantity, l, 'quantity')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.quantity, l, 'quantity'))}
                          size="small"
                          aria-label="Quantity"
                          type={lineValuesLocked ? 'text' : 'number'}
                          input={{ className: ns.numText }}
                          value={String(l.quantity)}
                          onChange={(_, d) => editor.setAmounts(l.id, { quantity: d.value })}
                          onFocus={() => { if (valuesLive) editor.focusField(`quantity:${l.key}`, String(l.quantity)) }}
                          onBlur={() => { if (valuesLive) editor.blurField(`quantity:${l.key}`, String(l.quantity)) }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={String(l.quantity ?? '—')} align="number" ariaLabel="Quantity" />
                    )}
                  </TableCell>
                  {/* The derived shares and the Total: locked everywhere (D-4).
                      The editor draws them as its own locked inputs. */}
                  {distributing && (
                    <TableCell className={boxedFields ? s.num : s.derivedNum}>
                      <DerivedMoney editing={editing} boxed={boxedFields} amount={shares.get(l.id)?.tax ?? 0} ariaLabel="Tax share" />
                    </TableCell>
                  )}
                  {distributing && (
                    <TableCell className={boxedFields ? s.num : s.derivedNum}>
                      <DerivedMoney editing={editing} boxed={boxedFields} amount={shares.get(l.id)?.shipping ?? 0} ariaLabel="Shipping share" />
                    </TableCell>
                  )}
                  <TableCell className={s.num}>
                    <DerivedMoney
                      editing={editing}
                      boxed={boxedFields}
                      amount={distributing ? (shares.get(l.id)?.total ?? toNumber(l.total)) : l.total}
                      ariaLabel="Total"
                    />
                  </TableCell>
                  <TableCell>
                    <div className={s.iconCell}>
                      <AdditionalDetailPopover
                        mountNode={popupMountNode}
                        sections={sections}
                        state={other}
                        hint={editing && !l.saved ? 'Save the invoice to fill these in.' : undefined}
                        marketing={{
                          categoryId: l.marketing.categoryId,
                          label: l.marketing.label,
                          onSelect: editable.marketing
                            ? (o) => (editor ? editor.pickCategory(l.id, o) : onCategoryChange?.(l.item, o.id))
                            : undefined,
                          saving,
                          pickerKey: `marketing:${pickerResetNonce}`,
                          describedBy: refusalFor(refusal, l.id, 'Marketing Category'),
                        }}
                        transportation={transportationDetail}
                      />
                      {invoice && (
                        <AssociatedEntitiesButton
                          mountNode={popupMountNode}
                          invoice={invoice}
                          lineItem={l.item}
                          state={entityState}
                          editable={tagsEditable}
                          unsaved={editing ? l.isNew : undefined}
                          onChanged={editor ? editor.tagsChanged : undefined}
                        />
                      )}
                      {editor && !lineValuesLocked && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteRegular />}
                          aria-label="Remove Line Item"
                          onClick={() => editor.removeRow(l.id)}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      {editor && !lineValuesLocked && (
        <div className={s.addRow}>
          <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={editor.addRow}>
            Add Line Item
          </Button>
        </div>
      )}
      <ReconcileFooter
        lineItemsSum={lines.reduce((sum, l) => sum + toNumber(l.total), 0)}
        knownLinesSum={sumKnownLineTotals(lines.map((l) => l.total))}
        subtotal={subtotal}
        tax={tax}
        shipping={shipping}
        total={total}
        canDistribute={amountsKnown}
        distributing={distributing}
        distribute={distribute}
        onDistributeChange={setDistribute}
      />
    </div>
  )
}

/** A derived amount — a charge share or a Total. The editor draws it as a
    locked input with a `$` before it, the read table as a locked box or text. */
function DerivedMoney({
  editing,
  boxed,
  amount,
  ariaLabel,
}: {
  editing: boolean
  boxed: boolean
  amount: number | string | null
  ariaLabel: string
}) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  if (!editing) return <LockedValue boxed={boxed} value={formatCurrency(amount)} align="money" ariaLabel={ariaLabel} />
  return (
    <Input
      className={s.cellInput}
      size="small"
      type="text"
      contentBefore="$"
      input={{ className: ms.moneyText }}
      aria-label={ariaLabel}
      value={typeof amount === 'number' ? groupedMoney(amount.toFixed(2)) : groupedMoney(String(amount ?? ''))}
      readOnly
      appearance="filled-darker"
    />
  )
}

function ReconcileFooter({
  lineItemsSum,
  knownLinesSum,
  subtotal,
  tax,
  shipping,
  total,
  canDistribute,
  distributing,
  distribute,
  onDistributeChange,
}: {
  lineItemsSum: number
  /** What `checkAmounts` compares: the lines that carry an amount, or `null`
      when none does. */
  knownLinesSum: number | null
  subtotal?: number | string | null
  tax?: number | string | null
  shipping?: number | string | null
  total?: number | string | null
  canDistribute: boolean
  /** The toggle is on AND there is something to spread: decides the label
      and the figure. `distribute` alone decides the switch's position. */
  distributing: boolean
  distribute: boolean
  onDistributeChange: (next: boolean) => void
}) {
  const s = useStyles()
  const ls = useSummaryLabelStyles()
  // The raw summed lines while off; those lines plus both shares while on —
  // with the vendor's variance of up to three cents, the figure the
  // distributed rows and the CSV sum to (D-151).
  const shown = distributing
    ? distributedGrandTotal(
        lineItemsSum,
        toNumber(tax),
        toNumber(shipping),
        total === undefined || total === null || String(total).trim() === '' ? null : toNumber(total),
      )
    : lineItemsSum
  // ALWAYS enabled once it renders (owner item 4, 2026-08-18): spreading a
  // zero charge is just zero shares.
  const toggle = canDistribute ? (
    <Switch
      size="small"
      labelPosition="before"
      label="View With Tax &amp; Shipping Distributed"
      checked={distribute}
      onChange={(_e, d) => onDistributeChange(d.checked)}
    />
  ) : null
  // Without invoice amounts there is nothing to reconcile against: no pill,
  // the same figure under the same label (owner, 2026-08-14).
  if (subtotal == null && tax == null && total == null && shipping == null) {
    return (
      <div className={s.footer}>
        <span className={ls.label}>Invoice Subtotal:</span>
        <span className={ls.amount}>{formatCurrency(lineItemsSum)}</span>
      </div>
    )
  }
  // The figure is the lines' own sum, labelled as such (owner, 2026-08-14).
  // The pill reads the RAW sum, never the distributed one: a view preference
  // must not turn a pill from red to green.
  const { reconciles } = checkAmounts(knownLinesSum, tax, total, shipping)
  return (
    <div className={s.footer}>
      <div className={s.footerControls}>
        {toggle}
        <DotPill pill={reconciles ? TINT_PILLS.green : TINT_PILLS.red}>
          {reconciles ? 'Reconciles' : "Doesn't reconcile"}
        </DotPill>
        <span className={ls.label}>{distributing ? 'Invoice Total:' : 'Invoice Subtotal:'}</span>
      </div>
      <span className={ls.amount}>{formatCurrency(shown)}</span>
    </div>
  )
}
